import React from 'react';
import {
    Box,
    Button,
    Grid,
    IconButton,
    Menu,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Menu as MenuIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { FONT_FAMILY_DISPLAY } from '../../theme';
import { goBackOrHome } from '../../utils';

type PropsType = {
    title?: string,
    subtitle?: string,
    hideBackButton?: boolean,
    addUrl?: string | null,
    addUrlTitle?: string,
    editUrl?: string | null,
    editUrlTitle?: string | null,
    CustomComponent?: JSX.Element | null
};

const Header: React.FC<PropsType> = (props) => {
    const {
        title,
        subtitle,
        addUrl,
        addUrlTitle,
        editUrl,
        editUrlTitle,
        hideBackButton = false,
        CustomComponent
    } = props;

    const navigate = useNavigate();
    const theme = useTheme();
    const isCompact = useMediaQuery(theme.breakpoints.down('md'));
    const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);

    const hasOverflowActions = !!CustomComponent || !!editUrl || !!addUrl;
    const hasActions = !hideBackButton || hasOverflowActions;

    const addButton = addUrl ? (
        <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(addUrl)}
        >
            {addUrlTitle || 'Skapa ny'}
        </Button>
    ) : null;

    const editButton = editUrl ? (
        <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(editUrl)}
        >
            {editUrlTitle || 'Redigera'}
        </Button>
    ) : null;

    return (
        <Grid container spacing={1} sx={{ pb: 2 }} alignItems="flex-end">
            <Grid item xs>
                {title && (
                    <Typography
                        variant="h4"
                        gutterBottom={!subtitle}
                        sx={{
                            fontFamily: FONT_FAMILY_DISPLAY,
                            fontWeight: 600,
                            letterSpacing: '-0.02em',
                            fontSize: { xs: '1.25rem', sm: '2.125rem' },
                            whiteSpace: { xs: 'normal', sm: 'nowrap' }
                        }}
                    >
                        {title}
                    </Typography>
                )}
                {subtitle && (
                    <Typography
                        variant="h6"
                        sx={{
                            fontSize: { xs: '1rem', sm: '1.25rem' },
                            whiteSpace: { xs: 'normal', sm: 'nowrap' }
                        }}
                    >
                        {subtitle}
                    </Typography>
                )}
            </Grid>
            {isCompact && hasOverflowActions && (
                <Grid item xs="auto">
                    <IconButton
                        aria-label="Åtgärder"
                        onClick={(event) => setMenuAnchor(event.currentTarget)}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Menu
                        anchorEl={menuAnchor}
                        open={Boolean(menuAnchor)}
                        onClose={() => setMenuAnchor(null)}
                        onClick={() => setMenuAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                                px: 1.5,
                                py: 1,
                                minWidth: 220,
                                '& span': {
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                    width: '100%'
                                },
                                '& .MuiButton-root': {
                                    ml: '0 !important',
                                    width: '100%',
                                    justifyContent: 'flex-start'
                                }
                            }}
                        >
                            {CustomComponent}
                            {editButton}
                            {addButton}
                        </Box>
                    </Menu>
                </Grid>
            )}
            {!isCompact && hasActions && (
                <Grid item xs={12} sm="auto">
                    <Grid
                        container
                        spacing={1}
                        alignItems="center"
                        justifyContent="flex-end"
                    >
                        {!hideBackButton && (
                            <Grid item>
                                <Button
                                    variant="text"
                                    startIcon={<ArrowBackIcon />}
                                    onClick={() => goBackOrHome(navigate)}
                                >
                                    Tillbaka
                                </Button>
                            </Grid>
                        )}
                        {CustomComponent && (
                            <Grid item>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 1,
                                        justifyContent: 'flex-end'
                                    }}
                                >
                                    {CustomComponent}
                                </Box>
                            </Grid>
                        )}
                        {editButton && (
                            <Grid item>
                                {editButton}
                            </Grid>
                        )}
                        {addButton && (
                            <Grid item>
                                {addButton}
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            )}
            {isCompact && !hideBackButton && (
                <Grid item xs={12}>
                    <Button
                        variant="text"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => goBackOrHome(navigate)}
                    >
                        Tillbaka
                    </Button>
                </Grid>
            )}
        </Grid>
    );
};

export default Header;
